class VariantSelector extends HTMLElement {
  // The selector belonging to `element` — the NEAREST one, not merely the first in
  // the document. A product card for the same product routinely renders earlier in
  // the DOM than the PDP itself: the header search drawer carries cards, and one of
  // them can be the product being viewed. Those cards render colour swatches only,
  // with no size group and therefore no required-option guard, so a document-wide
  // lookup binds the PDP's buy button to a selector that reports nothing missing —
  // which is how Add To Bag added the first available variant with no size chosen.
  //
  // Walking outward from `element` returns the match sharing the closest ancestor.
  // The final step reaches <html>, so this still covers the whole document when the
  // only match is elsewhere.
  static nearest(element, productId) {
    if (!element || !productId) return null;

    const query = `variant-selector[data-product-id="${productId}"]`;
    for (let node = element.parentElement; node; node = node.parentElement) {
      const match = node.querySelector(query);
      if (match) return match;
    }
    return null;
  }

  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const dataScript = this.querySelector("[data-variant-data]");
    if (!dataScript) return;

    try {
      this.variantData = JSON.parse(dataScript.textContent);
    } catch {
      return;
    }

    this.variants = this.variantData.variants;
    this.productOptions = this.variantData.options;
    this.productId = this.dataset.productId;
    this.optionButtons = [...this.querySelectorAll("[data-option-value]")];
    this.unselectIndices = new Set(
      [...this.querySelectorAll("[data-unselect-option]")].map((group) =>
        parseInt(group.dataset.optionGroup, 10),
      ),
    );
    this.selectedOptions = this.getInitialSelectedOptions();
    this.currentVariant = this.findVariant(this.selectedOptions);

    this.addEventListener("click", (event) => {
      const button = event.target.closest("[data-option-value]");
      if (!button) return;

      const index = parseInt(button.dataset.optionIndex, 10);
      const value = button.dataset.optionValue;

      this.selectedOptions[index] = value;
      // The shopper answered the prompt — drop the validation state immediately.
      this.clearMissingOptionError(index);

      const variant = this.findVariant(this.selectedOptions);
      this.currentVariant = variant;
      this.fillMissingOptions(variant);

      this.updateButtonStates();
      this.updateAvailability();

      publish(PUB_SUB_EVENTS.variantChange, {
        productId: this.productId,
        variant,
      });
    });

    this.updateAvailability();
  }

  disconnectedCallback() {
    this.initialized = false;
  }

  getInitialSelectedOptions() {
    const options = [];
    if (!this.productOptions) return options;

    this.productOptions.forEach((option, index) => {
      const activeButton = this.querySelector(
        `[data-option-index="${index}"][aria-pressed="true"]`,
      );
      if (activeButton) {
        options[index] = activeButton.dataset.optionValue;
      } else if (this.unselectIndices.has(index)) {
        options[index] = "";
      } else {
        options[index] = option.values?.[0] ?? "";
      }
    });

    return options;
  }

  findVariant(selectedOptions) {
    const exactMatch = this.variants.find((v) =>
      v.options.every(
        (opt, i) => !selectedOptions[i] || opt === selectedOptions[i],
      ),
    );
    if (exactMatch) return exactMatch;

    const displayedIndices = this.getDisplayedIndices();

    const partialMatch = this.variants.find((v) =>
      v.options.every((opt, i) => {
        if (!displayedIndices.has(i)) return true;
        return !selectedOptions[i] || opt === selectedOptions[i];
      }),
    );

    return partialMatch ?? null;
  }

  getMissingOptionName() {
    for (const index of this.unselectIndices) {
      if (!this.selectedOptions[index]) {
        return this.productOptions?.[index]?.name ?? null;
      }
    }
    return null;
  }

  isSelectionComplete() {
    return this.getMissingOptionName() === null;
  }

  // Public: is anything still buyable under the current partial selection? While a
  // required option (size) is unselected there is no chosen variant, and the one
  // findVariant resolves is only the first row matching what HAS been picked — a
  // sold-out XS would otherwise speak for the five sizes behind it. Used by the buy
  // button to decide its state before a size exists.
  hasAvailableInSelection() {
    const displayedIndices = this.getDisplayedIndices();

    return this.variants.some(
      (variant) =>
        variant.available &&
        variant.options.every(
          (opt, index) =>
            !displayedIndices.has(index) ||
            !this.selectedOptions[index] ||
            opt === this.selectedOptions[index],
        ),
    );
  }

  // Public: returns the fully-resolved variant, or null when the shopper still
  // has a required option (e.g. size) unselected. Used by other components
  // (e.g. pincode-check) that must act on a concrete variant.
  getSelectedVariant() {
    return this.isSelectionComplete() ? this.currentVariant : null;
  }

  shakeMissingOptions() {
    let firstPrompted = null;

    for (const index of this.unselectIndices) {
      if (this.selectedOptions[index]) continue;

      const group = this.querySelector(`[data-option-group="${index}"]`);
      const values = group?.querySelector("[data-option-values]");
      if (!values) continue;

      // Persistent state: the red message and the highlighted swatches stay put
      // until a value is chosen. The shake is the one-shot part on top.
      group.classList.add("is-invalid");
      group.querySelector("[data-option-error]")?.removeAttribute("hidden");
      firstPrompted ??= group;

      values.classList.remove("animate-shake");
      // Force reflow so re-adding the class restarts the animation on repeat clicks.
      void values.offsetWidth;
      values.classList.add("animate-shake");
      values.addEventListener(
        "animationend",
        () => values.classList.remove("animate-shake"),
        { once: true },
      );
    }

    this.revealPrompt(firstPrompted);
  }

  // The prompt is the only feedback there is, so it has to be on screen. Callers sit
  // well below the option groups — on mobile the pincode field is most of a screen
  // away — and a shake nobody can see reads as a dead button. Scrolls only when the
  // group is actually out of view, so the page does not jerk on every click.
  revealPrompt(group) {
    if (!group) return;

    const rect = group.getBoundingClientRect();
    if (rect.top >= 0 && rect.bottom <= window.innerHeight) return;

    group.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  clearMissingOptionError(index) {
    const group = this.querySelector(`[data-option-group="${index}"]`);
    if (!group) return;

    group.classList.remove("is-invalid");
    group.querySelector("[data-option-error]")?.setAttribute("hidden", "");
  }

  fillMissingOptions(variant) {
    if (!variant) return;
    const displayedIndices = this.getDisplayedIndices();
    variant.options.forEach((opt, i) => {
      if (!displayedIndices.has(i)) {
        this.selectedOptions[i] = opt;
      }
    });
  }

  getDisplayedIndices() {
    return new Set(
      this.optionButtons.map((btn) => parseInt(btn.dataset.optionIndex, 10)),
    );
  }

  updateButtonStates() {
    this.optionButtons.forEach((btn) => {
      const index = parseInt(btn.dataset.optionIndex, 10);
      const value = btn.dataset.optionValue;
      const isSelected = this.selectedOptions[index] === value;
      const isColor = btn.dataset.isColor === "true";

      btn.setAttribute("aria-pressed", isSelected ? "true" : "false");

      // aria-pressed still says which value is chosen, but a sold-out one never wears the
      // ordinary selected styling — however it came to be chosen, tapped directly or left
      // selected when the colour changed under it. Filled black reads as a successful,
      // buyable selection; .variant-sold-out is what says otherwise.
      const paintSelected = isSelected && this.isValueAvailable(index, value);

      if (isColor) {
        // border-border, NOT border-transparent, and it has to match what the Liquid renders
        // for an unselected swatch (variant-selector.liquid, the `paint_selected` ternary).
        // A white swatch on a white card is invisible without a ring, so the transparent
        // version was replaced there — but this line kept re-adding it on deselect, and since
        // both are single-class utilities the winner is whichever Tailwind emits last:
        // .border-transparent sits ~1KB after .border-border in the compiled file, so it won.
        // The result was a white swatch that had a ring until you selected it and picked
        // something else, then lost it for good.
        if (paintSelected) {
          btn.classList.add("border-foreground");
          btn.classList.remove("border-border", "hover:border-muted");
        } else {
          btn.classList.remove("border-foreground");
          btn.classList.add("border-border", "hover:border-muted");
        }
      } else if (btn.dataset.optionLayout === "grid") {
        if (paintSelected) {
          btn.classList.add(
            "border-foreground",
            "bg-foreground",
            "font-medium",
            "text-background",
          );
          btn.classList.remove("border-border", "text-foreground");
        } else {
          btn.classList.add("border-border", "text-foreground");
          btn.classList.remove(
            "border-foreground",
            "bg-foreground",
            "font-medium",
            "text-background",
          );
        }
      } else {
        const isFilled = btn.dataset.pillStyle === "filled";
        if (paintSelected) {
          btn.classList.add(
            "border-foreground",
            "bg-foreground",
            "text-background",
          );
          btn.classList.remove(
            "border-border",
            "border-foreground/20",
            "bg-foreground/5",
          );
        } else if (isFilled) {
          btn.classList.remove(
            "border-foreground",
            "bg-foreground",
            "text-background",
            "border-border",
          );
          btn.classList.add("border-foreground/20", "bg-foreground/5");
        } else {
          btn.classList.remove(
            "border-foreground",
            "bg-foreground",
            "text-background",
            "border-foreground/20",
            "bg-foreground/5",
          );
          btn.classList.add("border-border");
        }
      }
    });

    this.querySelectorAll("[data-selected-option]").forEach((label) => {
      const index = parseInt(label.dataset.selectedOption, 10);
      if (this.selectedOptions[index]) {
        label.textContent = this.selectedOptions[index];
      }
    });
  }

  // Whether a value can still be fulfilled given what is chosen around it.
  //
  // Availability cascades: a value is constrained only by the options BEFORE it,
  // never by later ones. So a colour stays clickable whenever any size of that
  // colour is in stock. Constraining it by the selected size too meant picking a
  // size that a colour happens to lack disabled that colour outright, leaving no
  // way to switch to it — and therefore no way to reach its available sizes.
  // Switching now resolves the sold-out variant normally, so the buy button
  // reports "Sold out" and the size grid shows what is left in that colour.
  //
  // snippets/variant-selector.liquid runs the same cascade server-side (constraint_1 /
  // constraint_2) so the first paint already agrees with this. Keep the two in step.
  isValueAvailable(index, value) {
    const displayedIndices = this.getDisplayedIndices();

    return this.variants.some(
      (v) =>
        v.available &&
        v.options.every((opt, i) => {
          if (!displayedIndices.has(i)) return true;
          if (i === index) return opt === value;
          if (i > index) return true;
          return !this.selectedOptions[i] || opt === this.selectedOptions[i];
        }),
    );
  }

  // Marks the values that cannot be fulfilled, without taking them out of reach.
  //
  // Deliberately not `disabled`. Selecting a sold-out size has to resolve to that variant so
  // the buy button can report "Sold out" — which is the whole point of tapping it — and a
  // disabled button swallows the tap and reports nothing at all, reading as broken. It is also
  // what the shopper already gets by switching colour until their size runs out, so the two
  // routes to the same state now behave the same. Adding to the bag stays blocked by
  // product-form.js's own availability check, which is where that decision belongs.
  updateAvailability() {
    for (const btn of this.optionButtons) {
      const index = parseInt(btn.dataset.optionIndex, 10);
      const soldOut = !this.isValueAvailable(index, btn.dataset.optionValue);
      btn.classList.toggle("variant-sold-out", soldOut);
    }
  }
}

if (!customElements.get("variant-selector")) {
  customElements.define("variant-selector", VariantSelector);
}
